import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, formatDetail } from "@/lib/api";

const GUEST_TOKEN_KEY = "ithaca_guest_token";
const GUEST_EMAIL_KEY = "ithaca_guest_email";

// Own axios instance so we never accidentally send the admin bearer token
// (which the shared `api` client picks up from localStorage).
const guestApi = axios.create({ baseURL: API });

function guestHeaders() {
  const token = localStorage.getItem(GUEST_TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function niceDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

function nightsBetween(a, b) {
  if (!a || !b) return 0;
  const d1 = new Date(a + "T00:00:00");
  const d2 = new Date(b + "T00:00:00");
  return Math.max(0, Math.round((d2 - d1) / 86400000));
}

// ============================================================
// Login form
// ============================================================
function LoginCard() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await guestApi.post("/guests/request-link", {
        email: email.trim().toLowerCase(),
        origin_url: window.location.origin,
      });
      setSent(true);
    } catch (e2) {
      setErr(formatDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-md mx-auto w-full">
      <div className="text-center mb-10">
        <div className="overline">Guest portal</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-4 font-light">Welcome back.</h1>
        <p className="text-ionian/60 mt-3 text-sm leading-relaxed">
          Enter the email you used to book. We'll send you a private sign-in link — no password needed.
        </p>
      </div>

      {sent ? (
        <div
          data-testid="guest-link-sent"
          className="border border-soft_border bg-white p-10 text-center"
        >
          <div className="overline text-brass">Check your inbox</div>
          <p className="text-ionian mt-4 leading-relaxed">
            If we have a stay on file for <strong>{email}</strong>, a private sign-in link is on its way.
          </p>
          <p className="text-ionian/60 mt-3 text-sm">The link is valid for 15 minutes.</p>
          <button
            onClick={() => { setSent(false); setEmail(""); }}
            className="mt-8 text-xs tracking-widest uppercase text-ionian/60 hover:text-brass"
            data-testid="guest-use-different-email"
          >
            Use a different email
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="border border-soft_border bg-white p-10 space-y-8">
          <div>
            <div className="overline mb-1">Email</div>
            <input
              data-testid="guest-email"
              required
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="editorial-input w-full"
              placeholder="you@example.com"
            />
          </div>
          {err && <div data-testid="guest-error" className="text-sm text-terracotta">{err}</div>}
          <button
            type="submit"
            disabled={loading}
            data-testid="guest-request-link"
            className="btn-ionian w-full py-3 rounded-full text-sm uppercase tracking-widest disabled:opacity-50"
          >
            {loading ? "Sending link…" : "Send my sign-in link"}
          </button>
          <p className="text-xs text-ionian/50 text-center leading-relaxed">
            Access is limited to guests who have completed a stay.
          </p>
        </form>
      )}

      <div className="text-center mt-8">
        <Link to="/" className="text-xs tracking-widest uppercase text-ionian/60 hover:text-brass">
          ← Back to the villa
        </Link>
      </div>
    </div>
  );
}

// ============================================================
// Authenticated portal
// ============================================================
function PortalDashboard({ onSignOut }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    guestApi.get("/guests/me", { headers: guestHeaders() })
      .then(r => { if (!ignore) { setData(r.data); setLoading(false); } })
      .catch(e => {
        if (ignore) return;
        setLoading(false);
        if (e.response?.status === 401) {
          onSignOut();
        } else {
          setErr(formatDetail(e.response?.data?.detail));
        }
      });
    return () => { ignore = true; };
  }, [onSignOut]);

  if (loading) {
    return (
      <div className="text-center text-ionian/60 py-24" data-testid="guest-loading">
        Loading your stays…
      </div>
    );
  }
  if (err) {
    return <div className="text-terracotta text-center py-24">{err}</div>;
  }
  if (!data) return null;

  const upcoming = (data.bookings || []).filter(b => b.is_upcoming);
  const past = (data.bookings || []).filter(b => !b.is_upcoming);

  return (
    <div className="max-w-4xl mx-auto w-full px-6 md:px-0" data-testid="guest-portal">
      <div className="flex items-start justify-between flex-wrap gap-6 mb-14">
        <div>
          <div className="overline">Your portal</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-3 font-light">
            Welcome back, {data.guest_name?.split(" ")[0] || "friend"}.
          </h1>
          <p className="text-ionian/60 mt-3 text-sm">{data.email}</p>
        </div>
        <button
          onClick={onSignOut}
          data-testid="guest-signout"
          className="text-xs tracking-widest uppercase text-ionian/60 hover:text-brass border-b border-transparent hover:border-brass pb-1"
        >
          Sign out
        </button>
      </div>

      {/* Welcome-back credit banner */}
      {data.welcome_back_code && (
        <div
          className="mb-14 border border-brass/40 bg-cream p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 justify-between"
          data-testid="guest-welcome-back"
        >
          <div>
            <div className="overline text-brass">A welcome-back gift, saved for you</div>
            <div className="font-serif text-2xl md:text-3xl mt-2 font-light">
              €{Math.round(data.welcome_back_code.discount_eur)} off your next stay
            </div>
            <p className="text-ionian/70 text-sm mt-3 leading-relaxed max-w-xl">
              Enter this code at checkout — it's tied to your email, so no one else can use it.
              {data.welcome_back_code.expires_at && (
                <> Valid until {niceDate(data.welcome_back_code.expires_at.slice(0,10))}.</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div
              className="font-mono text-lg tracking-widest bg-white border border-soft_border px-5 py-3 select-all"
              data-testid="guest-welcome-back-code"
            >
              {data.welcome_back_code.code}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(data.welcome_back_code.code);
                toast.success("Code copied");
              }}
              className="text-xs tracking-widest uppercase text-ionian/60 hover:text-brass"
              data-testid="guest-copy-welcome-back"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <section className="mb-16" data-testid="guest-upcoming">
          <div className="overline mb-6">Upcoming</div>
          <div className="space-y-4">
            {upcoming.map(b => <BookingRow key={b.id} b={b} upcoming />)}
          </div>
        </section>
      )}

      {/* Past */}
      <section className="mb-16" data-testid="guest-past">
        <div className="overline mb-6">Your stays</div>
        {past.length === 0 && (
          <div className="text-ionian/50 italic">
            You haven't stayed with us yet — but you're welcome soon.
          </div>
        )}
        <div className="space-y-4">
          {past.map(b => <BookingRow key={b.id} b={b} />)}
        </div>
      </section>

      {/* Share code */}
      {data.share_code && (
        <section
          className="mb-16 border-t border-soft_border pt-12"
          data-testid="guest-share-code"
        >
          <div className="overline">Share the garden</div>
          <h2 className="font-serif text-3xl mt-3 font-light">
            €{Math.round(data.share_code.discount_eur)} off — for a friend.
          </h2>
          <p className="text-ionian/70 text-sm mt-4 leading-relaxed max-w-xl">
            Pass this code on to someone you'd love to see in Kioni. It's good for {data.share_code.max_uses} bookings
            ({data.share_code.max_uses - (data.share_code.uses_count || 0)} left) and takes
            €{Math.round(data.share_code.discount_eur)} off their nightly total.
          </p>
          <div className="mt-6 flex items-center gap-4">
            <div
              className="font-mono text-lg tracking-widest bg-white border border-soft_border px-5 py-3 select-all"
              data-testid="guest-share-code-value"
            >
              {data.share_code.code}
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(data.share_code.code);
                toast.success("Code copied");
              }}
              className="text-xs tracking-widest uppercase text-ionian/60 hover:text-brass"
              data-testid="guest-copy-share"
            >
              Copy
            </button>
          </div>
        </section>
      )}

      <div className="text-center mt-16 mb-24">
        <Link
          to="/"
          className="text-xs tracking-widest uppercase text-ionian/60 hover:text-brass"
        >
          ← Back to the villa
        </Link>
      </div>
    </div>
  );
}

function BookingRow({ b, upcoming }) {
  const nights = b.nights || nightsBetween(b.checkin, b.checkout);
  const rebookHref = `/booking?checkin=${b.checkin}&checkout=${b.checkout}&guests=${b.guests || 2}`;
  return (
    <div
      className="border border-soft_border bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between"
      data-testid={`booking-row-${b.id}`}
    >
      <div>
        <div className="font-serif text-xl md:text-2xl font-light">
          {niceDate(b.checkin)} <span className="text-ionian/40">→</span> {niceDate(b.checkout)}
        </div>
        <div className="text-sm text-ionian/60 mt-2">
          {nights} night{nights === 1 ? "" : "s"} · {b.guests || 2} guest{(b.guests || 2) === 1 ? "" : "s"}
          {b.amount ? ` · €${Math.round(b.amount)}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-4">
        {upcoming ? (
          <span className="text-xs tracking-widest uppercase text-brass">Confirmed</span>
        ) : (
          <Link
            to={rebookHref}
            className="text-xs tracking-widest uppercase text-ionian/70 hover:text-brass border-b border-transparent hover:border-brass pb-1"
            data-testid={`rebook-${b.id}`}
          >
            Book these dates again →
          </Link>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Root page
// ============================================================
export default function GuestPortal() {
  const [signedIn, setSignedIn] = useState(!!localStorage.getItem(GUEST_TOKEN_KEY));

  // Show any error the pre-React bootstrap left behind (e.g. expired magic link).
  useEffect(() => {
    const err = sessionStorage.getItem("ithaca_magic_error");
    if (err) {
      sessionStorage.removeItem("ithaca_magic_error");
      toast.error(err);
    }
  }, []);

  function signOut() {
    localStorage.removeItem(GUEST_TOKEN_KEY);
    localStorage.removeItem(GUEST_EMAIL_KEY);
    setSignedIn(false);
  }

  return (
    <div className="min-h-screen bg-stone_white py-20 md:py-28 px-6 flex flex-col">
      {signedIn ? (
        <PortalDashboard onSignOut={signOut} />
      ) : (
        <LoginCard />
      )}
    </div>
  );
}
